import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { catchError, forkJoin, lastValueFrom, map, Observable } from 'rxjs';
import { WellTypeService } from 'src/app/wellbore-inputs/services/well-type-datums.service';
import { PeriforOnChangeMessages, SignalRService } from '../../../shared/services/signal-r.service';
import { PoreFrac, PpfgPlotUi } from '../../models/poreFrac.model';
import { PoreAndFracService } from '../../services/pore-and-frac.service';
import { WellConfigService } from '../../../shared/services/well-config.service';
import { TrajectoryService } from 'src/app/shared/services/trajectory.service';
import { FluidsService } from '../../../perical/services/fluids.service';
import { FormationTopService } from 'src/app/wellbore-inputs/services/formation-top.service';
import { FormationTop } from 'src/app/perical/models/formation.model';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { Store } from '@ngneat/elf';
import { SessionStorageKeys, StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { GetPpgFromValue, GetValueFromPpg, GetValueFromPsi } from '../../shared/helpers/units.helper';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { CurrentAppIdsDto } from 'src/app/core/models/current-app-ids-dto';

@Component({
  selector: 'app-ppfg-plot',
  template: `
    <app-tool-loader [isLoading]="isLoading()" positionUnset="true">
      <p-toolbar>
          <ng-template #start>
            <div class="flex items-center gap-2 toolbar-menu">
              <p-selectButton
                [options]="pressureEmw"
                [ngModel]="pressureEmwSelected"
                (onChange)="tablePlotToggle($event)"
                [allowEmpty]="false"
              ></p-selectButton>

              <div class="flex items-center gap-2">
                <p-checkbox
                  inputId="formationTops"
                  size="small"
                  [ngModel]="plotFormationTops"
                  (onChange)="toggleFormationTops($event)"
                  binary="true"
                  class="h-full"
                >
                </p-checkbox>
                <label class="pt-1" for="formationTops">Show Formation Tops</label>
              </div>
            </div>
          </ng-template>
      </p-toolbar>

      @if (!isLoading()) {
        <app-xy-line-plot
          [plotData]="plot.data"
          [xAxisTitle]="xAxisTitle"
          [yAxisTitle]="yAxisTitle"
          [plotName]="plotName"
          [downloadPlotName]="downloadPlotName"
          [plotTitle]="'PPFG Plot'"
          [xyLinePlotStore]="ppfgPlotStore"
          [annotations]="annotations"
          [shapes]="shapes"
          [plotName]="'PPFG Plot'"
        ></app-xy-line-plot>
      }
    </app-tool-loader>
  `,
  standalone: false
})
export class PpfgPlotComponent implements OnInit, OnDestroy {
  private _userUnits: UserUnitsModel;

  private selectedString: string;

  public casingArray: { hanger: number, shoe: number, name: string, seqNumber: number }[] = [];
  public casingArrayMudWeight: { top: number, bottom: number, mudWeight: number, seqNumber: number }[] = [];
  public isLoading = signal<boolean>(false);
  public columnDefsPlot: any[] = [
    { header: 'EMW', field: 'equivalentMudWeight' }
  ];
  public pressureEmw: SelectItem[] = [
    { label: 'EMW', value: 'emw' },
    { label: 'Pressure', value: 'pressure' }
  ];
  public porePressures: PoreFrac[];
  public fracturePressures: PoreFrac[];
  private _formationTops: FormationTop[]
  private _isOffshore: boolean;
  public mudlineDepth: number;
  public annotations: any[] = [];
  public shapes: any[] = [];
  public plot = {
    data: []
  };

  public yAxisTitle = '';
  public xAxisTitle = '';
  public plotName;
  public downloadPlotName = 'pore_pressure_plot';

  public pressureEmwSelected: string = 'emw';
  public plotFormationTops: boolean = false;

  //State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.ppfgPlotStore = this._storeService.createStore(this.componentId, new PpfgPlotUi, withUIEntities<XyLinePlotUi>());
    this.pressureEmwSelected = this.ppfgPlotStore.state.pressureEmwSelected || 'emw';
    this.plotFormationTops = this.ppfgPlotStore.state.plotFormationTops ?? false;
  }
  get componentId(): string {
    return this._componentId;
  }
  public ppfgPlotStore: Store;

  constructor(
    private _poreAndFracService: PoreAndFracService,
    private _wellTypeService: WellTypeService,
    private _signalRService: SignalRService,
    private _wellConfigService: WellConfigService,
    private _trajectoryService: TrajectoryService,
    private _fluidsService: FluidsService,
    private _formationTopService: FormationTopService,
    private _storeService: StoreService
  ) {}

  async ngOnInit(): Promise<void> {
    this._userUnits = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
    const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
    this.selectedString = appIds.tubularId;
    this.isLoading.set(true);
    this.getTubularStrings();
  }

  signalRfunc(data: any) {
    if (data.action == PeriforOnChangeMessages.REFRESH_PORE_PRESSURE_INPUTS ||
      data.action === PeriforOnChangeMessages.REFRESH_TUBULARS_LIST ||
      data.action === PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      this.casingArrayMudWeight = [];
      const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
      this.selectedString = appIds.tubularId;
      this.isLoading.set(true);
      this.getTubularStrings();
    }
  }

  setPlotName(): string {
    return 'poreFracPressurePlot - ' + this.selectedString + ' - ' + this.pressureEmwSelected + (this.plotFormationTops ? ' - Formation Tops' : '');
  }


  getTubularStrings() {
    this.isLoading.set(true);
    const sources = [
      this._poreAndFracService.getPorePressures() as Observable<any>,
      this._poreAndFracService.getFractureGradient() as Observable<any>,
      this._wellTypeService.getWellType() as Observable<any>,
      this._wellConfigService.getTubulars(),
      this._formationTopService.getAllFormationTops()
    ];

    forkJoin(sources).pipe(
      map(([porePressures, fractureGradient, wellType, tubulars, formationTops]) => {
        this.porePressures = porePressures;
        this.fracturePressures = fractureGradient;
        this.mudlineDepth = +(wellType.drillFloorElevation + wellType.waterDepth).toFixed(2);
        this._formationTops = formationTops;
        this._isOffshore = wellType.type == 'Subsea' || wellType.type == 'Platform';

        if (tubulars.length > 0) {
          this.casingArray = [];
          for (const tubular of tubulars) {
            if (tubular.type === 'Tubing' || tubular.type === 'Tieback') {
              continue;
            }
            this.getData(tubular);
          }
        } else {
          this.plotData();
          this.plotName = this.setPlotName();
        }
      }),
      catchError(err => {
        this.isLoading.set(false);
        return err;
      })).subscribe();
  }

  async getData(tubular: any) {
    const stringSections = tubular.stringSections;
    const bottomCasingDepth = stringSections[stringSections.length - 1].bottomMeasuredDepth;
    const topCasingDepth = tubular.hangerMd;

    const tvds = await lastValueFrom(
      this._trajectoryService.getTvdsFromMds([bottomCasingDepth, topCasingDepth])
    );

    const bottom = tvds[0];
    const top = tvds[1];

    try {
      const annularFluid = await lastValueFrom(this._fluidsService.getFluidById(tubular.annularFluidId)) as any;
      const density = annularFluid.state.nominalDensity > 0 ? annularFluid.state.nominalDensity : undefined;

      this.casingArrayMudWeight.push({ top, bottom, mudWeight: density, seqNumber: tubular.sequenceNumber });
      this.casingArrayMudWeight.sort((a, b) => (a.seqNumber > b.seqNumber ? 1 : -1));

      this.casingArray.push({ hanger: top, shoe: bottom, name: tubular.name, seqNumber: tubular.sequenceNumber });
      this.casingArray.sort((a, b) => (a.seqNumber > b.seqNumber ? 1 : -1));

      this.plotData();
    } catch (err) {
      console.error("Error fetching fluid data:", err);
    } finally {
      this.isLoading.set(false);
    }
  }

  tablePlotToggle(e): void {
    this.pressureEmwSelected = e.value;
    this.ppfgPlotStore.update(state => ({ ...state, pressureEmwSelected: e.value }));
    this.plotName = 'poreFracPressurePlot - ' + this.pressureEmwSelected;
    this.plotData();
  }

  plotData() {
    const poreData = this.porePressures;
    const fracData = this.fracturePressures;
    if (poreData) {
      this.parseData(poreData, fracData);
    }
  }

  parseData(poreData: any[], fracData: any[]) {
    if (poreData === undefined) { return; }
    let minX = 50;
    const plotData = [];
    for (let i = 0; i < 2; i++) {
      const data = i == 0 ? poreData : fracData;
      const traceArray = [];
      const trueVerticalDepths = data.map(r => r.trueVerticalDepth);
      const pressures = data.map(r => r.pressure);
      const emws = data.map(r => r.equivalentMudWeight);
      const mudlineIndex = trueVerticalDepths.findIndex(r => r >= this.mudlineDepth);
      trueVerticalDepths.splice(0, mudlineIndex);
      pressures.splice(0, mudlineIndex);
      emws.splice(0, mudlineIndex);
      if (trueVerticalDepths[0] > this.mudlineDepth) {
        trueVerticalDepths.unshift(this.mudlineDepth);
        pressures.unshift(0);
        emws.unshift(emws[0]);
      }

      const minXCheck = Math.min(...emws);
      if (minXCheck < minX) {
        minX = minXCheck;
      }

      this.columnDefsPlot.forEach((element => {
        traceArray.push(
          {
            name: element.header,
            x: this.pressureEmwSelected === 'emw' ? emws : pressures,
            y: trueVerticalDepths,
          });
      }));

      plotData[i] = traceArray;
    }

    const mudWeightLine = [];
    const csgArrayLength = this.casingArrayMudWeight.length;
    let casing: any;
    for (let i = 0; i < csgArrayLength; i++) {
      const top = this.casingArrayMudWeight[i].top;
      const bottom = this.casingArrayMudWeight[i].bottom;
      const mudWeight = this.casingArrayMudWeight[i].mudWeight;

      if (i == 0) {
        casing = {
          name: 'Mud Weight',
          x: [mudWeight, mudWeight],
          y: [top, bottom],
        }
      } else {
        casing.x.push(mudWeight);
        casing.y.push(this.casingArrayMudWeight[i - 1].bottom);

        casing.x.push(mudWeight);
        casing.y.push(bottom);
      }
      mudWeightLine.push(casing);
    }

    this.plotPorePressure(plotData, mudWeightLine, casing, minX);
    this.isLoading.set(false);
  }

  plotPorePressure(plotData, mudWeightLine, casing, minX) {
    const plot = [];

    let data;
    for (let i = 0; i < 2; i++) {
      data = plotData[i];

      const pressureData = {
        name: i == 0 ? 'Pore Pressure ' : 'Fracture Pressure',
        x: data[0].x,
        y: data[0].y
      }

      plot.push(pressureData);
    }

    let maxX = Math.max(...data[0].x);

    let pressureFactorMin = 0;
    let pressureFactorMax = 0;
    switch (this._userUnits.pressure) {
      case 'psi':
        pressureFactorMin = 1000;
        pressureFactorMax = 2500;
        break;
      case 'KPa':
        pressureFactorMin = 6000;
        pressureFactorMax = 15000;
        break;
      case 'bar':
        pressureFactorMin = 170;
        pressureFactorMax = -500;
        break;
      case 'atm':
        pressureFactorMin = 150;
        pressureFactorMax = 250;
        break;
    }

    let densityFactorMin = 0;
    let densityFactorMax = 0;
    switch (unitsLib[this._userUnits.density].symbol) {
      case 'ppg':
        densityFactorMin = 0.5;
        densityFactorMax = 1.5;
        break;
      case 'kg/m³':
      case 'g/L':
        densityFactorMin = 100;
        densityFactorMax = 500;
        break;
      case 'g/cm³':
      case 'kg/l':
      case 'sg':
        densityFactorMin = 0.5;
        densityFactorMax = 0.5;
        break;
      case 'g/m³':
        densityFactorMin = 60000;
        densityFactorMax = 180000;
        break;
    }

    const isPressure = this.pressureEmwSelected === 'pressure';
    const xAxisFactorMudlineMin = isPressure ? pressureFactorMin : densityFactorMin;
    const xAxisFactorMudlineMax = isPressure ? pressureFactorMax : densityFactorMax;

    this.shapes = [
      {
        type: "line",
        xref: "paper",
        x0: 0,
        y0: this.mudlineDepth,
        x1: 1,
        y1: this.mudlineDepth,
        line: {
          color: '#783F04'
        }
      }
    ];

    this.annotations = [
      {
        showarrow: false,
        text: this._isOffshore ? "Mudline" : "Ground Level",
        font: {
          size: 13,
          color: '#783F04'
        },
        xref: "paper",
        align: "right",
        x: 1,
        xanchor: "right",
        y: this.mudlineDepth,
        yanchor: "bottom",
      }
    ];

    if (!isPressure) {
      plot.push(mudWeightLine[0]);
    }
    const lastElement = plot[0].x.length - 1;
    if (!plot[0].x[lastElement]) {
      plot[0].x.pop();
      plot[0].y.pop();
    }
    const densitySymbol = unitsLib[this._userUnits.density].symbol;
    const allPlottedXValues = plot
      .flatMap(trace => Array.isArray(trace.x) ? trace.x : [])
      .filter((value: unknown): value is number => typeof value === 'number' && Number.isFinite(value));
    const smallestPointExt = allPlottedXValues.length ? Math.min(...allPlottedXValues) : minX;
    const largestPointExt = allPlottedXValues.length ? Math.max(...allPlottedXValues) : maxX;
    const xAxisCasingIncrement = isPressure ? GetValueFromPsi(700, this._userUnits.pressure) : GetValueFromPpg(0.5, densitySymbol);
    const pressureIncrement = isPressure ? GetValueFromPsi(450, this._userUnits.pressure) : GetValueFromPpg(1, densitySymbol);
    const casingX = largestPointExt + pressureIncrement;
    const csgArrayLength = this.casingArray.length;
    const casingStartOffset = isPressure ? GetValueFromPsi(1, this._userUnits.pressure) : GetValueFromPpg(1, densitySymbol);
    const fullPlotMaxX = csgArrayLength > 0
      ? casingX + casingStartOffset + ((csgArrayLength - 1) * xAxisCasingIncrement)
      : largestPointExt;
    const fullPlotMinX = smallestPointExt;

    if (this.plotFormationTops) {
      const formationColors: string[] = [
        '#743E07',
        '#9C560F',
        '#D27617',
        '#8E5D2A',
        '#6D4F31',
        '#A36A2F',
        '#EC8316',
        '#A96F34'
      ];

      let x = 0;
      this._formationTops.forEach(formTop => {
        const top = {
          name: formTop.formation['name'] + ' Top',
          y: [formTop.formationTop, formTop.formationTop],
          x: [fullPlotMinX - xAxisFactorMudlineMin - GetPpgFromValue(0.15, densitySymbol), Math.max(maxX, fullPlotMaxX) + xAxisFactorMudlineMax],
          mode: 'lines',
          hoverinfo: 'none',
          line: {
            width: 1.5,
            dash: 'dot',
            color: formationColors[x]
          },
        }
        x++;
        if (x >= formationColors.length) { x = 0; }
        plot.push(top);
      });
    }

    let distance = casingStartOffset;
    for (let i = 0; i < csgArrayLength; i++) {
      const hanger = this.casingArray[i].hanger;
      const shoe = this.casingArray[i].shoe;
      const name = this.casingArray[i].name;

      const casing = {
        name: name,
        x: [casingX + distance, casingX + distance],
        y: [hanger, shoe],
        marker: {
          size: 10,
          symbol: ["line-ew", "triangle-up-dot"]
        },
        line: {
          color: '#999999'
        },
        hovertemplate: `TVD: %{y} ${this._userUnits.longLengths}`,
        showlegend: false
      }
      distance += xAxisCasingIncrement;

      plot.push(casing);
    }

    this.plot.data = plot;
    this.xAxisTitle = this.pressureEmwSelected === 'emw'
      ? `EMW (${unitsLib[this._userUnits.density].symbol})`
      : `Pressure (${unitsLib[this._userUnits.pressure].symbol})`;
    this.yAxisTitle = `TVD (${this._userUnits.longLengths})`;
  }

  public toggleFormationTops(e) {
    this.plotFormationTops = e.checked;
    this.ppfgPlotStore.update(state => ({ ...state, plotFormationTops: e.checked }));
    this.plotName = this.setPlotName();

    this.plotData();
  }

  ngOnDestroy() {
    this.signalRfunc = null;
  }
}
